// Ensure @material-ui/core is initialized before any module-scope styled() calls
// that wrap MUI components (Rolldown CJS lazy-init ordering workaround)
import '@material-ui/core/TextField';
import '@material-ui/core/TableContainer';
import '@material-ui/core/TableCell';
import '@material-ui/core/TableRow';
import '@material-ui/core/Chip';
import * as Sentry from '@sentry/react';
import {
  clearLeavingPage,
  isChunkLoadError,
  isLeavingPage,
  isReloading,
  markLeavingPage,
  tryReloadOnce,
} from './utils/chunk-error';
import { httpErrorContext } from './utils/error-context';

function normalizeReason(reason: unknown): string {
  if (reason instanceof Error) return reason.message;
  if (typeof reason === 'string') return reason;
  if (reason == null) return '';
  if (typeof reason === 'object') {
    try {
      return JSON.stringify(reason);
    } catch {
      return Object.prototype.toString.call(reason);
    }
  }
  return String(reason);
}

// Reload bloqueado pelo TTL: deploy provavelmente quebrado. Reporta (o beforeSend
// filtra o erro original, então este marcador explícito é o que chega ao Sentry).
// Se ESTE ciclo de página já agendou o reload, o bloqueio é só o 2º import
// falhando no mesmo carregamento (um deploy derruba vários chunks de uma vez):
// o reload já está a caminho e não há nada a reportar. Era 41 de 43 eventos
// no primeiro deploy medido (14/10).
function reportBlockedChunkRecovery(context: string, data: Record<string, unknown>) {
  if (isReloading()) return;
  Sentry.withScope((scope) => {
    scope.setTag('chunk_recovery_status', 'reload_blocked_by_ttl');
    scope.setTag('chunk_recovery_context', context);
    scope.setContext('chunk_error', data);
    Sentry.captureException(new Error(`Chunk recovery blocked by TTL (${context})`));
  });
}

// Auto-reload when stale JS chunks are requested after a deployment.
// Covers: HTML served as JS (SyntaxError '<'), dynamic import() failures,
// CSS preload failures, and webpack legacy chunk errors.
//
// Exceto quando o documento está indo embora (isLeavingPage): aí o import()
// falhou porque a navegação o abortou, não por deploy. Nem recarrega (cancelaria
// a ida pro portal) nem reporta. Os redirects do app marcam a saída antes de
// navegar; o `pagehide` cobre qualquer outra saída (link externo, fechar aba).
window.addEventListener('pagehide', markLeavingPage);
window.addEventListener('pageshow', (event: PageTransitionEvent) => {
  if (event.persisted) clearLeavingPage();
});

// --- Erro síncrono (script load) ---
window.addEventListener(
  'error',
  (event: ErrorEvent) => {
    if (!isChunkLoadError(event.message || '')) return;

    event.stopImmediatePropagation(); // always suppress — we own the reporting from here

    if (isLeavingPage()) return;
    if (tryReloadOnce('global-error-handler')) return;
    reportBlockedChunkRecovery('global-error-handler', {
      message: event.message || '',
      filename: event.filename || '',
      lineno: event.lineno || 0,
      colno: event.colno || 0,
    });
  },
  true, // capture phase — fires before Sentry's bubble-phase listener
);

// --- Camada B: intercepta o erro de preload ANTES do React ---
// O helper de preload do Vite dispara `vite:preloadError` (cancelable) quando um
// import() dinâmico falha, ANTES de a rejeição subir pro React Router / boundary.
// Disparar o reload aqui adianta a recuperação e marca `reloadInFlight`, então os
// boundaries já renderizam neutro (spinner) caso cheguem a montar.
//
// IMPORTANTE: não chamar preventDefault(). O helper faz `e().catch(i)` e `i()` só
// relança se NÃO for prevenido. Se prevenirmos, a promise resolve com `undefined`,
// o React.lazy recebe `undefined` e estoura "Cannot read properties of undefined" —
// erro que NÃO casa com isChunkLoadError e cairia na tela genérica. Deixamos relançar.
window.addEventListener('vite:preloadError', (event: Event) => {
  const payload = (event as Event & { payload?: unknown }).payload;

  if (isLeavingPage()) return;
  if (tryReloadOnce('vite-preload-error')) return; // reload a caminho, done

  reportBlockedChunkRecovery('vite-preload-error', { message: normalizeReason(payload) });
});

// --- Erro assíncrono (dynamic import / Vite lazy routes) ---
window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
  const reason = e.reason;
  const msg = normalizeReason(reason);

  if (!isChunkLoadError(msg)) return;

  e.preventDefault(); // always suppress — we own the reporting from here

  if (isLeavingPage()) return;
  if (tryReloadOnce('unhandled-rejection')) return; // reload triggered, done

  reportBlockedChunkRecovery('unhandled-rejection', {
    message: msg,
    reason: reason instanceof Error
      ? { name: reason.name, message: reason.message }
      : msg,
  });
});

// Disable Sentry locally: if DSN is missing OR NX_ENV is DEV/LOCAL, don't init.
// Allows turning off Sentry alerts in local dev by commenting NX_SENTRY_DSN
// in .env.local, or by setting NX_ENV=LOCAL.
const SENTRY_DISABLED_ENVS = new Set(['DEV', 'LOCAL']);
const sentryEnabled =
  !!process.env['NX_SENTRY_DSN'] &&
  !SENTRY_DISABLED_ENVS.has(process.env['NX_ENV'] ?? '');

// O DSN aponta pro Better Stack Errors (formato do Sentry, escrito pelo
// inframo/stacks/observability em SSM /<env>/dashboard-web/errors/dsn): app
// dashboard-web-sandbox em sandbox/preview e dashboard-web-prod em prod. O SDK
// continua o do Sentry; só o valor muda.
const ENV = process.env['NX_ENV'] ?? '';

Sentry.init({
  enabled: sentryEnabled,
  dsn: process.env['NX_SENTRY_DSN'],
  environment: ENV,
  // SHA curto do build (COMMIT_REF do Netlify, via vite.config): casa com o
  // `service.version` que o dashboard-api loga e com o source map subido.
  release: process.env['NX_APP_VERSION'],

  // Sem PII automática (IP inferido, cookies, headers). Quem é o usuário vem do
  // setUser explícito no login (id + e-mail do operador da marca, o mesmo que já
  // vai pro PostHog/Mixpanel) e é o que o suporte usa pra achar a pessoa.
  sendDefaultPii: false,

  // Do browser saem ERROS e os spans de navegação/clique (pageload,
  // navigation, ui.interaction.click e as requests): é o que mostra, no trace,
  // que botão ou mudança de página gerou as requests do backend. Replay e
  // web events ficam desligados (custo). Sem `tracesSampleRate` o header
  // `sentry-trace` ainda sairia, mas sem o span pai: o backend viraria raiz.
  // Perfil "frontend": em prod o foco é erro (o Errors captura 100% dos erros
  // por conta própria) e só 10% das interações viram trace; fora de prod,
  // todas. O browser não sabe de antemão se a request vai ser lenta ou
  // falhar, então não há amostragem por resultado aqui. O trace_id por
  // interação vai sempre no traceparent nosso (amostrado ou não), então o
  // backend segue agrupado por clique.
  integrations: [
    Sentry.browserTracingIntegration({
      // Long task / long animation frame não entram no trace (ruído e custo).
      enableLongTask: false,
      enableLongAnimationFrame: false,
      // INP (`ui.interaction.press <unknown>`) também não: virava raiz de
      // requests que não eram dele e duplicava o nosso `ui.action`.
      enableInp: false,
    }),
  ],
  tracesSampleRate: ENV === 'PROD' ? 0.1 : 1.0,
  // Só o que conta a história fica no trace: pageload, navigation, ui.action,
  // clique (INP) e http.client. Medido em 18/09: 87% dos spans do browser eram
  // resource.* (chunk, css, imagem) + browser.*/paint/mark — 1,3 MB por dia
  // de nada.
  ignoreSpans: [
    { op: /^resource./ },
    { op: /^browser./ },
    { op: /^(paint|mark|measure)$/ },
    { op: /^ui.long-/ },
    { op: /^ui.interaction/ },
  ],

  enableLogs: false,

  // Erro lançado por extensão do navegador não é nosso: em 14/10 uma extensão
  // do Edge mandou 26 eventos `reading 'M_ID'` com o stack inteiro dentro dela.
  denyUrls: [
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /^safari-(web-)?extension:\/\//,
  ],

  beforeSend(event, hint) {
    const msg = event.exception?.values?.[0]?.value ?? '';
    if (isChunkLoadError(msg)) return null;
    // AxiosError solto (promise sem catch) chegava só como "Request failed
    // with status code 403", todos no mesmo grupo e sem dizer de qual
    // endpoint. Anexa a chamada (sem body, header nem query) e agrupa por
    // método + rota + status, com ids trocados por :id.
    const err = hint?.originalException as { isAxiosError?: boolean } | undefined;
    if (err?.isAxiosError) {
      const http = httpErrorContext(err);
      event.contexts = { ...event.contexts, http_call: http };
      const route = (http.path ?? '')
        .replace(/^https?:\/\/[^/]+/, '')
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id')
        .replace(/\/\d+(?=\/|$)/g, '/:id');
      event.fingerprint = [
        'axios',
        http.method ?? '',
        route,
        String(http.status ?? http.error_code ?? ''),
      ];
    }
    return event;
  },

  // Breadcrumbs só no ERRO (onde a linha do tempo antes da falha vale). No
  // span de interação eles eram 91% dos bytes (medido 21/09: 3,2 MB de
  // 3,5 MB em 3 dias) e repetiam, a cada clique, o buffer INTEIRO da sessão
  // (~65 por span: mixpanel, cognito, console…). As requests da interação
  // já estão no trace como spans http.client, com status e duração.
  beforeSendTransaction(event) {
    delete event.breadcrumbs;
    return event;
  },
  // E o que nem no erro ajuda: telemetria de terceiros e o marcador interno
  // de transação do SDK.
  beforeBreadcrumb(crumb) {
    if (crumb.category === 'sentry.transaction') return null;
    const url = String(crumb.data?.['url'] ?? '');
    if (/mixpanel\.com|cognito-idp\.|docs\.abbiamolog\.com/.test(url)) return null;
    return crumb;
  },

  // Quem recebe `sentry-trace`/`baggage`: o dashboard-api (prod, sandbox e
  // os stages pr-*/dev-* no execute-api). O @abbiamo/utils 3.1.2 continua
  // esse trace_id no backend; é o que liga a sessão do usuário aos eventos
  // do dashboard-api e de quem ele chama (care etc).
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/dashboard-api\.abbiamo\.io/,
    /^https:\/\/sandbox-dashboard-api\.abbiamo\.io/,
    /^https:\/\/[a-z0-9]+\.execute-api\.us-east-1\.amazonaws\.com\//,
  ],
});
